import { SaveButton } from '@/components/_common';
import {
  useReportSetings,
  useUpdateReportSettings,
} from '@/core/domain/enterprise/ReportSettings/ReportSettings.hooks';
import {
  MatrixSettingsRequestInterface,
  ReportDefaultCodeEnum,
} from '@/core/domain/enterprise/ReportSettings/ReportSettings.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import { PageProps } from '@/types/PageProps';
import { Col, Divider, Form, Radio, Row, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export default function ReportSettingsPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const { data } = useReportSetings();
  const [form] = useForm<MatrixSettingsRequestInterface>();
  const [disabled, setDisabled] = useDisclosure(true);
  const { width } = useWindowSize();

  const { mutateAsync } = useUpdateReportSettings();

  const notationsOptions = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
  ];

  const reportDefaultCodeOptions = [
    { label: 'Código padrão', value: ReportDefaultCodeEnum.DEFAULT_CODE },
    {
      label: 'Descrição do item',
      value: ReportDefaultCodeEnum.ITEM_DESCRIPTION,
    },
    { label: 'Por inserção', value: ReportDefaultCodeEnum.INSERTION_ORDER },
  ];

  const reportCustomerInfoOptions = notationsOptions;

  const reportDefaultValueOptions = [
    { label: 'Valor base', value: true },
    { label: 'Valor líquido', value: false },
  ];

  useEffect(() => {
    if (data) form.setFieldsValue(data);
    setDisabled.close();
  }, [data]);

  const handleUpdateReportSettings = async () => {
    setDisabled.open();
    try {
      if (data) {
        const values = await form.getFieldsValue();
        mutateAsync({ id: data.id, ...values }).then(() => setDisabled.close());
      }
    } catch {
      setDisabled.close();
    }
  };

  return (
    <>
      <Helmet title={intl({ id: route.name })} />
      <div className="page">
        <div className="page-header">
          <Typography.Title level={4}>Relatório</Typography.Title>
          {!(width < 992) && (
            <SaveButton
              onClick={handleUpdateReportSettings}
              disabled={disabled}
            >
              {intl({
                id: 'commons.form.button.save',
              })}
            </SaveButton>
          )}
        </div>

        <Divider />

        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={24}>
              <Typography.Title level={5}>
                Relatório de orçamento
              </Typography.Title>

              <Form.Item
                name="reportAnnotations"
                label="Exibir quadro de anotações em relatório de oficina"
              >
                <Radio.Group
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={notationsOptions}
                />
              </Form.Item>

              <Form.Item
                name="reportSorting"
                label="Ordenar relatórios administrativos por"
              >
                <Radio.Group
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={reportDefaultCodeOptions}
                />
              </Form.Item>

              <Form.Item
                name="reportServiceSections"
                label="Separar itens da listagem de serviços e peças do relatório administrativo por área"
              >
                <Radio.Group
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={notationsOptions}
                />
              </Form.Item>

              <Form.Item
                name="reportCustomerInfo"
                label="Exibir informações do cliente em relatório de oficina"
              >
                <Radio.Group
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={reportCustomerInfoOptions}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={24}>
              <Typography.Title level={5}>
                Relatório de comissão
              </Typography.Title>

              <Form.Item
                name="reportDefaultValue"
                label="Selecione o valor a ser utilizado no cálculo da comissão"
              >
                <Radio.Group
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={reportDefaultValueOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {width < 992 && (
          <SaveButton
            block
            onClick={handleUpdateReportSettings}
            disabled={disabled}
          >
            {intl({
              id: 'commons.form.button.save',
            })}
          </SaveButton>
        )}
      </div>
    </>
  );
}
